/**
 * 確認ダイアログ
 *
 * 呼び出しは `await confirmDialog({...})`（@/lib/confirm-dialog）。表示は App 直下の ConfirmDialogHost が 1 つだけ受け持つ。
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AuthImage } from '@/components/ui/auth-image'
import { buttonVariants } from '@/components/ui/button-variants'
import { type ConfirmOptions, useConfirmDialogStore } from '@/store/useConfirmDialogStore'

/** react-modal-sheet（常設シート・プレイヤー）より手前に出す */
const ABOVE_SHEET = 'z-[10000000]'

type Props = ConfirmOptions & {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
}

export const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel,
  destructive = false,
  imageSrc,
  onCancel,
  onConfirm,
}: Props) => (
  <AlertDialog open={open} onOpenChange={(next) => !next && onCancel()}>
    <AlertDialogContent className={ABOVE_SHEET} overlayClassName={ABOVE_SHEET}>
      <AlertDialogHeader>
        {imageSrc && <AuthImage src={imageSrc} alt="" lazy={false} className="h-40 w-full rounded-md object-contain" />}
        <AlertDialogTitle>{title}</AlertDialogTitle>
        {description && <AlertDialogDescription className="whitespace-pre-line">{description}</AlertDialogDescription>}
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>キャンセル</AlertDialogCancel>
        <AlertDialogAction
          className={destructive ? buttonVariants({ variant: 'destructive' }) : undefined}
          onClick={onConfirm}
        >
          {confirmLabel ?? (destructive ? '削除' : 'OK')}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
)

export const ConfirmDialogHost = () => {
  const options = useConfirmDialogStore((s) => s.options)
  const isOpen = useConfirmDialogStore((s) => s.isOpen)
  const settle = useConfirmDialogStore((s) => s.settle)
  if (!options) return null
  return <ConfirmDialog {...options} open={isOpen} onCancel={() => settle(false)} onConfirm={() => settle(true)} />
}
